import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const TrendsSettingsContext = createContext();

export const useTrendsSettings = () => {
  const context = useContext(TrendsSettingsContext);
  if (!context) {
    throw new Error('useTrendsSettings must be used within a TrendsSettingsProvider');
  }
  return context;
};

export const TrendsSettingsProvider = ({ children }) => {
  // Инициализируем настройки из localStorage или используем значения по умолчанию
  const initializeSettings = () => {
    const saved = localStorage.getItem('trendsSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          visibleLines: parsed.visibleLines || getDefaultVisibleLines(),
          counterLines: parsed.counterLines || {},
          counterBucket: parsed.counterBucket === 'day' ? 'day' : 'hour',
        };
      } catch (e) {
        console.warn('Error parsing saved trends settings:', e);
      }
    }
    return {
      visibleLines: getDefaultVisibleLines(),
      counterLines: {},
      counterBucket: 'hour',
    };
  };

  // Получаем настройки легенды по умолчанию
  const getDefaultVisibleLines = () => ({
    PV: true,
    SV: true,
    PWM: true,
    Amper: true,
    PV2: true
  });

  const [settings, setSettings] = useState(initializeSettings);

  // Сохраняем изменения в localStorage
  useEffect(() => {
    localStorage.setItem('trendsSettings', JSON.stringify(settings));
  }, [settings]);

  const toggleLine = (key) => {
    setSettings(prev => ({
      ...prev,
      visibleLines: {
        ...prev.visibleLines,
        [key]: !prev.visibleLines[key]
      }
    }));
  };

  const setVisibleLines = (visibleLines) => {
    setSettings(prev => ({ ...prev, visibleLines }));
  };

  const toggleCounter = (key) => {
    setSettings((prev) => ({
      ...prev,
      counterLines: {
        ...(prev.counterLines || {}),
        [key]: !prev.counterLines?.[key],
      },
    }));
  };

  const setCounterBucket = (bucket) => {
    setSettings((prev) => ({
      ...prev,
      counterBucket: bucket === 'day' ? 'day' : 'hour',
    }));
  };

  const resetToDefaults = () => {
    setSettings({
      visibleLines: getDefaultVisibleLines(),
      counterLines: {},
      counterBucket: 'hour',
    });
  };

  const value = {
    visibleLines: settings.visibleLines,
    counterLines: settings.counterLines || {},
    counterBucket: settings.counterBucket === 'day' ? 'day' : 'hour',
    toggleLine,
    toggleCounter,
    setCounterBucket,
    setVisibleLines,
    resetToDefaults
  };

  return (
    <TrendsSettingsContext.Provider value={value}>
      {children}
    </TrendsSettingsContext.Provider>
  );
};

TrendsSettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}; 